/**
 * What a Copilot (beta) answer may turn into on the page. ACOP-158 (SEC-29, AVS-A2-SUPPORT-COPILOT-6D1888).
 *
 * Deliberately free of React and of react-markdown so the rules can be unit-tested directly — the same
 * reasoning as utils/answerLinks.ts and utils/nav.ts.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 *
 * The answer is text written by a model, and the model repeats whatever it was shown: a knowledge
 * article, a ticket, a change request. Anyone who can put words in front of the model can put words in
 * an answer, so the page must treat the answer as hostile. The security review found that in the
 * original chat, which turned the answer back into markup. That chat is gone (ACOP-152). Today the
 * answer goes through react-markdown, which escapes raw HTML to plain text and strips a link whose
 * scheme is not on its own short list. That is true today, but only as a library default that no test
 * here pinned, and one that a dependency bump or a one-line plugin would remove without anyone noticing.
 *
 * So the decisions are written down here and handed to the renderer explicitly (CopilotTwo.tsx), and
 * AnswerLink checks them again where the link is drawn. The two places agree because they call the same
 * function.
 *
 * ── The rules ──────────────────────────────────────────────────────────────────────────────────
 *
 * - A link is live only if it is an `https://` address. Everything else (`javascript:`, `data:`,
 *   `vbscript:`, `http:`, `mailto:`, a relative path, a protocol-relative `//host`) is not a link. The
 *   click handler sends every link to the linked-content page, which only frames absolute web addresses,
 *   so `mailto:` and relative links were already dead ends before this.
 * - A `#N` link is a source-number link; whether it opens anything is AnswerLink's call, not ours.
 * - An address a browser would quietly repair (a tab or newline inside `java\tscript:`, a zero-width
 *   character, a space) is refused rather than repaired: nobody writes those by accident.
 * - An address with a user name (`https://kb.avsight.com@evil.example/`) is refused: it is the standard
 *   way to make one host look like another.
 * - The only page elements an answer may produce are the ones its formatting needs. No picture (an
 *   address the model chooses, fetched the moment the answer shows, is a way to carry the chat out of the
 *   page), no frame, form, input, style or script. An element that is not on the list is replaced by its
 *   text.
 */

/** Characters that have no business in an address: C0/C1 controls and space, soft hyphen, zero-width and bidirectional marks, line/paragraph separators, BOM. */
// eslint-disable-next-line no-control-regex -- finding control characters is the whole point of this pattern
const BROKEN_OR_INVISIBLE = /[\u0000-\u0020\u007f-\u009f\u00ad\u200b-\u200f\u2028-\u202e\u2060-\u2064\ufeff]/;

/** `https://` followed by something that can start a host (not another slash or a backslash, which a browser would swallow). */
const HTTPS_WITH_HOST = /^https:\/\/[^/\\?#]/i;

/** A user name or password in the part of the address before the first `/`, `?` or `#` (including a bare `@`). */
const CREDENTIALS = /^https:\/\/[^/?#]*@/i;

/**
 * The address to put in a live link, or `null` when the text is not a safe link.
 *
 * Returns the address as written (with any surrounding spaces removed), not a rewritten one, so a link
 * that is safe looks exactly as the model wrote it.
 */
export function safeAnswerHref(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const href = raw.trim();
  if (!HTTPS_WITH_HOST.test(href) || BROKEN_OR_INVISIBLE.test(href) || CREDENTIALS.test(href)) return null;
  try {
    const url = new URL(href);
    return url.protocol === "https:" && url.hostname !== "" && url.username === "" && url.password === "" ? href : null;
  } catch {
    return null;
  }
}

/**
 * The `urlTransform` handed to react-markdown: the one place that decides what address an answer's link
 * ends up with. A `#…` link passes (AnswerLink turns `#N` into a source link, and any other fragment into
 * plain text); a safe `https` address passes; anything else becomes an empty address, which AnswerLink
 * draws as plain text.
 */
export function answerUrlTransform(url: string): string {
  if (url.startsWith("#")) return url;
  return safeAnswerHref(url) ?? "";
}

/**
 * The page elements an answer may produce — everything react-markdown's parser makes without the table
 * and task-list plugins this app does not install, minus the picture. Handed to react-markdown as
 * `allowedElements` with `unwrapDisallowed`, so a disallowed element keeps its text.
 */
export const ANSWER_ALLOWED_ELEMENTS: readonly string[] = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "a",
  "code",
  "pre",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
];
